import classNames from 'classnames';
import {
  type CSSProperties,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useState,
} from 'react';

import styles from './Input.module.css';

type Size = 'sm' | 'md' | 'lg';

export type InputCssVar =
  | '--input-disabled-bg'
  | '--input-disabled-border'
  | '--input-disabled-color'
  | '--input-disabled-placeholder-color';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size?: Size;
  isInvalid?: boolean;
  startSlot?: ReactNode;
  endSlot?: ReactNode;
  wrapperClassName?: string;
  wrapperStyle?: CSSProperties;
  cssVars?: Partial<Record<InputCssVar, string>>;
  'data-testid'?: string;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'md',
      isInvalid,
      disabled,
      startSlot,
      endSlot,
      className,
      wrapperClassName,
      wrapperStyle,
      cssVars,
      'data-testid': dataTestId,
      onFocus,
      onBlur,
      ...inputProps
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);

    return (
      <div
        className={classNames(styles.wrapper, styles[size], wrapperClassName)}
        style={{ ...wrapperStyle, ...(cssVars as CSSProperties) }}
        data-testid={dataTestId}
        data-focused={focused || undefined}
        data-disabled={disabled || undefined}
        data-invalid={isInvalid || undefined}
      >
        <div className={styles.control}>
          {startSlot ? (
            <span className={styles.startSlot}>{startSlot}</span>
          ) : null}
          <input
            ref={ref}
            className={classNames(styles.input, className)}
            disabled={disabled}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            {...inputProps}
          />
          {endSlot ? <span className={styles.endSlot}>{endSlot}</span> : null}
        </div>
      </div>
    );
  }
);

Input.displayName = 'Input';
